/**
 * FRONT-721: the one place a timestamp becomes words in this app.
 *
 * Fourteen surfaces each hand-rolled a `toLocaleString('de-DE', { timeZone:
 * 'Europe/Berlin', … })`, so every tenant read one developer's locale and one
 * developer's clock: a user in New York saw a briefing labelled with Berlin's
 * hours and a date written the German way round. The same class of bug was
 * already fixed once on the Inspector dialogs (BUG-012) by simply dropping both
 * arguments — this makes that the shared rule instead of a local one.
 *
 * The reader's own device is the source, deliberately: it's what the browser
 * already knows, what `lib/timezone.ts` already sends to the server on every
 * request (FRONT-1507), and it needs no setting anybody has to fill in. Passing
 * `undefined` as the locale is what asks Intl for the device's own — an empty
 * options object still has to omit `timeZone` entirely, or the device zone is
 * overridden again.
 */

const DEVICE_LOCALE = undefined;

/** Date and time, e.g. "19 Aug 2026, 14:32" in the reader's own conventions. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const d = toDate(value);
  return d ? d.toLocaleString(DEVICE_LOCALE, options) : '';
}

/** Date only, e.g. "19 Aug 2026". */
export function formatDate(
  value: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString(DEVICE_LOCALE, options) : '';
}

/** Clock time only, e.g. "14:32". */
export function formatTime(
  value: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
): string {
  const d = toDate(value);
  return d ? d.toLocaleTimeString(DEVICE_LOCALE, options) : '';
}

/** The reader's own IANA zone, for the rare label that has to name it. */
export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * FRONT-398: a feed row's stamp. Inside a day group the heading already says
 * which day it was, so the row shows a bare clock; a row that sits OUTSIDE any
 * day group (the "Worth revisiting" section, a chat card) has to say the date
 * too, or "14:32" on a conversation nobody has touched for three weeks means
 * nothing.
 */
export function formatRowStamp(
  value: string | number | Date | null | undefined,
  opts: { withDate?: boolean } = {},
): string {
  return opts.withDate
    ? formatDateTime(value, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : formatTime(value);
}

/**
 * FRONT-2489: a stamp that stands on its own — a bare clock when the moment
 * is from the reader's own today, the day as well (and the year, when it
 * isn't this one) otherwise. "08:56" on a five-day-old chat reads as this
 * morning; this is the one rule for any surface with no day heading above it.
 */
export function formatStamp(
  value: string | number | Date | null | undefined,
  now: Date = new Date(),
): string {
  const d = toDate(value);
  if (!d) return '';
  if (d.toDateString() === now.toDateString()) return formatTime(d);
  return formatDateTime(d, {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * FRONT-2489: a chat saved as a document carries a server-written stat line
 * ("Title — 2 messages · 1 reply · last active 2026-08-14"). The ISO day is
 * stored locale-neutral on purpose; this rewrites it in the reader's own date
 * style and drops the title echo when the row already prints the title.
 */
export function humanizeSummaryDates(text: string, title?: string | null): string {
  let out = text;
  const t = title?.trim();
  if (t && out.startsWith(`${t} — `)) out = out.slice(t.length + 3);
  const thisYear = new Date().getFullYear();
  return out.replace(/\blast active (\d{4})-(\d{2})-(\d{2})\b/g, (_, y, m, day) =>
    `last active ${formatDate(new Date(Number(y), Number(m) - 1, Number(day)), {
      day: 'numeric',
      month: 'short',
      // Same rule as `formatStamp`: the year only when it isn't this one.
      ...(Number(y) === thisYear ? {} : { year: 'numeric' }),
    })}`,
  );
}
